<template>
    <div class="button-grid">
        <!-- Row 1 -->
        <div class="button-row state-row">
            <VButton
                :class="reservationAcceptanceStatusButtonClass"
                class="grid-button acceptance-status rounded state-button"
            >
                {{ reservationAcceptanceStatusButtonText }}
            </VButton>

            <VButton
                v-if="reservationAssignmentState != null"
                :class="reservationAssignmentStatusButtonClass"
                class="grid-button assignment-status rounded state-button"
            >
                {{ reservationAssignmentStatusButtonText }}
            </VButton>

            <VButton
                v-if="reservationAttendanceState != null"
                :class="reservationAttendanceStatusButtonClass"
                class="grid-button attendance-status rounded state-button"
            >
                {{ reservationAttendanceStatusButtonText }}
            </VButton>
        </div>

        <!-- Row 2 -->
        <div class="button-row">
            <VButton
                v-if="props.reservationDetails.futureStateActions.showAcceptOrDeclineActions"
                class="grid-button acceptance-status declined rounded"
                @click="declineTableReservation"
            >
                {{ translations.reservationStatesGrid.decline }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showAcceptOrDeclineActions"
                class="grid-button acceptance-status accepted rounded"
                @click="acceptTableReservation"
            >
                {{ translations.reservationStatesGrid.accept }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showConfirmAssignmentAction"
                class="grid-button acceptance-status assigned rounded"
                @click="confirmTableAssignment"
            >
                {{ translations.general.confirm }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showSetAssignmentTableAsFixedAction"
                class="grid-button acceptance-status fixed rounded"
                @click="setFixedTableAssignment"
            >
                {{ translations.reservationStatesGrid.fix }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showRemoveFixedAssignmentAction"
                class="grid-button acceptance-status assigned rounded"
                @click="removeFixedTableAssignment"
            >
                {{ translations.reservationStatesGrid.removeFix }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showSetNotArrivedAction"
                class="grid-button acceptance-status notarrived rounded"
                @click="setNotArrivedAttendance"
            >
                {{ translations.enums.reservationAttendanceState.NotArrived }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showSetPartiallyArrivedAction"
                class="grid-button acceptance-status partiallyarrived rounded"
                @click="setPartiallyArrivedAttendance"
            >
                {{ translations.enums.reservationAttendanceState.PartiallyArrived }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showSetArrivedAction"
                class="grid-button acceptance-status arrived rounded"
                @click="setArrivedAttendance"
            >
                {{ translations.enums.reservationAttendanceState.Arrived }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showSetPartiallySeatedAction"
                class="grid-button acceptance-status partiallyseated rounded"
                @click="setPartiallySeatedAttendance"
            >
                {{ translations.enums.reservationAttendanceState.PartiallySeated }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showSetSeatedAction"
                class="grid-button acceptance-status seated rounded"
                @click="setSeatedAttendance"
            >
                {{ translations.enums.reservationAttendanceState.Seated }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showSetNoShowAction"
                class="grid-button acceptance-status noshow rounded"
                @click="setNoShowAttendance"
            >
                {{ translations.enums.reservationAttendanceState.NoShow }}
            </VButton>

            <VButton
                v-if="props.reservationDetails.futureStateActions.showCompleteAttendanceAction"
                class="grid-button acceptance-status completed rounded"
                @click="setCompletedAttendance"
            >
                {{ translations.enums.reservationAttendanceState.Completed }}
            </VButton>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { OrderDao } from "@/data/dao/service/OrderDao";
    import {
        ReservationAcceptanceState,
        ReservationAssignmentState,
        ReservationAttendanceState,
    } from "@/data/model/Enums";
    import type { IReservationDetails } from "@/data/model/Order";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed } from "vue";

    import VButton from "@/components/elements/VButton.vue";

    const orderDao = Factory.getInstance<OrderDao>(OrderDao);
    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);

    const props = defineProps<{
        orderId: string;
        reservationDetails: IReservationDetails;
    }>();

    const emit = defineEmits(["onOperationFailed", "onReservationStateChanged"]);

    // Reactive

    // first row
    const reservationAcceptanceState = computed<ReservationAcceptanceState>(
        () => props.reservationDetails.acceptanceState
    );
    const reservationAssignmentState = computed<ReservationAssignmentState | undefined>(
        () => props.reservationDetails.assignmentState
    );
    const reservationAttendanceState = computed<ReservationAttendanceState | undefined>(
        () => props.reservationDetails.attendanceState
    );

    // computed
    const reservationAcceptanceStatusButtonClass = computed(() => {
        if (reservationAcceptanceState.value != null)
            return ReservationAcceptanceState[reservationAcceptanceState.value].toLowerCase();
        else return "";
    });

    const reservationAcceptanceStatusButtonText = computed(() => {
        return translations.enums.reservationAcceptanceState[
            ReservationAcceptanceState[reservationAcceptanceState.value]
        ];
    });

    const reservationAssignmentStatusButtonClass = computed(() => {
        if (reservationAssignmentState.value != null)
            return ReservationAssignmentState[reservationAssignmentState.value].toLowerCase();
        else return "";
    });

    const reservationAssignmentStatusButtonText = computed(() => {
        if (reservationAssignmentState.value != null)
            return translations.enums.reservationAssignmentState[
                ReservationAssignmentState[reservationAssignmentState.value]
            ];
        return "";
    });

    const reservationAttendanceStatusButtonClass = computed(() => {
        if (reservationAttendanceState.value != null)
            return ReservationAttendanceState[reservationAttendanceState.value].toLowerCase();
        else return "";
    });

    const reservationAttendanceStatusButtonText = computed(() => {
        if (reservationAttendanceState.value != null)
            return translations.enums.reservationAttendanceState[
                ReservationAttendanceState[reservationAttendanceState.value]
            ];
        return "";
    });

    async function setFixedTableAssignment() {
        try {
            const updatedReservationDetails = await orderDao.setFixedTableAssignment({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }
    async function confirmTableAssignment() {
        try {
            const updatedReservationDetails = await orderDao.confirmTableAssignment({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function removeFixedTableAssignment() {
        try {
            const updatedReservationDetails = await orderDao.removeFixedTableAssignment({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function setPartiallyArrivedAttendance() {
        try {
            const updatedReservationDetails = await orderDao.setPartiallyArrivedAttendance({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function setNotArrivedAttendance() {
        try {
            const updatedReservationDetails = await orderDao.setNotArrivedAttendance({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function setArrivedAttendance() {
        try {
            const updatedReservationDetails = await orderDao.setArrivedAttendance({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function setPartiallySeatedAttendance() {
        try {
            const updatedReservationDetails = await orderDao.setPartiallySeatedAttendance({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function setSeatedAttendance() {
        try {
            const updatedReservationDetails = await orderDao.setSeatedAttendance({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function setCompletedAttendance() {
        try {
            const updatedReservationDetails = await orderDao.setCompletedAttendance({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function declineTableReservation() {
        try {
            const updatedReservationDetails = await operatorDao.declineTableReservation({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function acceptTableReservation() {
        try {
            const updatedReservationDetails = await operatorDao.acceptTableReservation({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }

    async function setNoShowAttendance() {
        try {
            const updatedReservationDetails = await orderDao.setNoShowAttendance({
                orderId: props.orderId,
                tableReservationId: props.reservationDetails.tableReservationId,
            });
            emit("onReservationStateChanged", updatedReservationDetails);
        } catch (error) {
            logError(error);
            emit("onOperationFailed");
        }
    }
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    td {
        padding: 2px;
    }

    .button-grid {
        display: flex;
        flex-direction: column;
        gap: 12px;
        width: 100%;
    }

    .button-row {
        display: flex;
        flex-direction: row;
        justify-content: start;
        gap: 6px;
        flex-wrap: wrap;
    }

    .grid-button {
        width: auto;
        font-size: 14px;
        font-weight: 500;
        background-color: #2980b9;
        color: colors.$white;
        border-color: #2980b9;

        &.state-button {
            cursor: default;
            pointer-events: none;
            transition: none;
            opacity: 0.6;
        }

        &.open {
            background-color: colors.$tastierDarkGrey;
            border-color: colors.$tastierDarkGrey;
            color: colors.$white;
        }

        &.pendingoperator {
            background-color: colors.$red; //DARK PINK?
            border-color: colors.$red;
            color: colors.$white;
        }

        &.pendingenduser {
            background-color: colors.$orange;
            border-color: colors.$orange;
            color: colors.$white;
        }

        &.accepted {
            background-color: colors.$green;
            border-color: colors.$green;
            color: colors.$white;
        }

        &.declined,
        &.noshow {
            background-color: colors.$pink;
            border-color: colors.$pink;
            color: colors.$white;
        }

        &.unassigned {
            background-color: colors.$red;
            border-color: colors.$red;
            color: colors.$white;
        }

        &.tentative,
        &.partiallyarrived,
        &.partiallyseated {
            background-color: colors.$orange;
            border-color: colors.$orange;
            color: colors.$white;
        }

        &.assigned,
        &.arrived,
        &.seated,
        &.completed {
            background-color: colors.$green;
            border-color: colors.$green;
            color: colors.$white;
        }

        &.fixed {
            background-color: colors.$tastierDarkGrey;
            border-color: colors.$tastierDarkGrey;
            color: colors.$white;
        }

        &.notarrived {
            background-color: colors.$tastierLightGrey;
            border-color: colors.$tastierLightGrey;
            color: colors.$black;
        }
    }
</style>
